import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import { StatusCode } from "api/protocols";
import Toggle from "components/form/toggle/Toggle";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";

import { Select } from "components/select/index";

import { useTranslation } from "react-i18next";

import { PostFolder } from "api/rpc/2024-04/facilityAdmin/product/folder";
import { GetRegisterGroups } from "api/rpc/2024-04/facilityAdmin/facility/register/group";
import Sheet from "components/sheet/Sheet";
import EmojiGrid, { allIcons } from "components/IconSelector/iconSelector";
import { faSmile } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Portal from "elements/Portal";

interface IFolderState {
  title: string;
  default: boolean;
  registers: Array<Record<string, any>>;
  register_group_id: number;
  icon_name: string;
}

interface INewIconState {
  isModalOpen: boolean;
  icon_name: string;
}

export default function FoldersNew() {
  const { Option } = Select;
  const history = useHistory();
  const { t } = useTranslation();
  const [folderState, setFolderState] = useState<IFolderState>({
    title: "",
    default: false,
    registers: [],
    register_group_id: null,
    icon_name: "",
  });

  const [iconState, setIconState] = useState<INewIconState>({
    isModalOpen: false,
    icon_name: "",
  });

  useEffect(() => {
    void loadRegisters();
  }, []);

  function openingIconModal() {
    setIconState(prevState => ({ ...prevState, isModalOpen: true }));
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setFolderState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleChange(event: any) {
    const { id, checked } = event.target;
    setFolderState(prevState => ({ ...prevState, [id]: checked }));
  }

  async function saveNewFolder() {
    const params = {
      title: folderState.title,
      default: folderState.default,
      register_group_id: folderState.register_group_id,
      icon_name: folderState.icon_name,
    };

    const res = await PostFolder(params, true);
    if (res.status !== Number(StatusCode.OK)) {
      return;
    }

    console.log(res);
    history.push("/admin/settings/folders");
  }

  async function loadRegisters() {
    const registersRes = await GetRegisterGroups(null, true);
    if (registersRes.status !== StatusCode.OK) {
      console.log(registersRes.message);
      return;
    }
    setFolderState(prevState => ({ ...prevState, registers: registersRes.data }));
  }

  function handleRegisterSelect(register_group_id: number) {
    setFolderState(prevState => ({ ...prevState, register_group_id }));
  }

  const primaryAction = {
    content: t("Save"),
    action: saveNewFolder,
  };

  const secondaryAction = {
    content: (
      <>
        <FontAwesomeIcon icon={faSmile} style={{ marginRight: "8px" }} /> {/* Smile icon */}
        Add Icon
      </>
    ),
    action: () => openingIconModal(),
  };

  // This function is passed to EmojiGrid to handle icon selection
  function handleIconSelect(icon: string) {
    setIconState(prevState => ({ ...prevState, icon_name: icon }));
  }

  function handleIconConfirm() {
    setFolderState(prevState => ({ ...prevState, icon_name: iconState.icon_name }));
    setIconState(prevState => ({ ...prevState, isModalOpen: false, icon_name: "" }));
  }

  return (
    <Page
      title={t("New Folder")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={[secondaryAction]} // Add secondary action here
      titleMetadata={
        folderState.icon_name && (
          <FontAwesomeIcon icon={allIcons[folderState.icon_name]} style={{ marginRight: "8px" }} />
        )
      }
      breadcrumbs={[
        { prefix: true, label: t("Folders"), url: "/admin/settings/folders" },
      ]}
    >
      <Card>
        <Card.Section>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={folderState.title}
                  label={t("Title")}
                  id="title"
                  onChange={handleInputChange}
                  placeholder={t("Title")}
                  className="mb-2"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Select
                  label={t("Register Group")}
                  onChange={handleRegisterSelect}
                  defaultValue={folderState.registers?.length > 0 ? folderState.registers[0]?.id : null}
                >
                  {folderState.registers?.map((register, index) => {
                    return (
                      <Option value={register?.id} name={register?.title} key={index}>
                        <span>{register?.title}</span>
                      </Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                <Toggle
                  label={t("Default folder")}
                  id="default"
                  checked={folderState.default}
                  onChange={handleToggleChange}
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
      </Card>
      <Portal isMounted={iconState.isModalOpen}>
        <Sheet
          title={t("Add Icon")}
          supportText={t("Choose an icon to help visually identify this folder category")}
          size="medium"
          open={iconState.isModalOpen}
          onOk={handleIconConfirm}
          okText={t("Add")}
          closable
          onCancel={() => setIconState(prevState => ({ ...prevState, isModalOpen: false, icon_name: "" }))}
        >
          <EmojiGrid onIconSelect={handleIconSelect} selectedIcon={folderState.icon_name} />
        </Sheet>
      </Portal>
    </Page>
  );
}
